/**
 * HelpDocsTab — the Help overlay's Docs tab (Day 100b.2).
 *
 * Renders the in-app help corpus inside the 320px overlay:
 *   - an active slug WITH a published row → the article via the shared
 *     <MarkdownView> (BODY ONLY; no "On this page" rail — too narrow; in-body
 *     #anchors still resolve) + a one-line "Last updated";
 *   - an active slug with NO published row → a per-slug "coming soon" naming the
 *     surface (item 9 — the corpus is authored at Day 103b);
 *   - no active slug → a search box (title-weighted token-prefix over the corpus)
 *     + the browse list.
 *
 * Data comes from the shared useDocsPages() loader (1A); search from the pure
 * docsSearch() (2B). Internal Related-link nav routes back through onSelectSlug
 * (the MarkdownView 1D prop) instead of opening a new tab.
 */
import { useState, type JSX } from 'react';
import { FileText, Search } from 'lucide-react';
import MarkdownView from '../ui/MarkdownView';
import { useDocsPages } from '../../hooks/useDocsPages';
import { searchDocs } from '../../lib/docsSearch';

// Short "last updated" date (docs_pages.updated_at) — mirrors HelpDocs.fmtDate.
function fmtDate(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Human-readable surface name from a bare slug, for the coming-soon card (drops a
// trailing "-overview"; title-cases the words). "purchasing-overview" →
// "Purchasing"; "receive-a-purchase-order" → "Receive A Purchase Order".
function humanizeSlug(slug: string): string {
  return slug
    .replace(/-overview$/, '')
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

interface HelpDocsTabProps {
  slug: string | null;
  onSelectSlug: (slug: string | null) => void;
}

export default function HelpDocsTab({ slug, onSelectSlug }: HelpDocsTabProps): JSX.Element {
  const { rows, loading } = useDocsPages();
  const [query, setQuery] = useState<string>('');

  if (loading) {
    return <div className="p-4 text-sm text-ink-muted">Loading…</div>;
  }

  const selected = slug ? (rows.find((r) => r.slug === slug) ?? null) : null;

  // Active slug WITH a published row → the article (body only).
  if (selected) {
    return (
      <div className="p-4">
        <button
          type="button"
          onClick={() => onSelectSlug(null)}
          className="mb-3 text-xs text-ink-muted hover:text-ink-primary transition-colors cursor-pointer"
        >
          ← All articles
        </button>
        <p className="mb-3 text-2xs text-ink-muted">Last updated {fmtDate(selected.updated_at)}</p>
        <MarkdownView markdown={selected.body} onInternalLink={(s) => onSelectSlug(s)} />
      </div>
    );
  }

  // Active slug with NO published row → per-slug coming-soon (names the surface).
  if (slug) {
    return (
      <div className="p-6 text-center">
        <FileText size={20} className="mx-auto mb-2 text-ink-muted" />
        <p className="text-sm font-medium text-ink-bright">{humanizeSlug(slug)}</p>
        <p className="mt-1 text-sm text-ink-secondary">Help for this page is coming soon.</p>
        <button
          type="button"
          onClick={() => onSelectSlug(null)}
          className="mt-3 text-xs text-ink-muted hover:text-ink-primary transition-colors cursor-pointer"
        >
          Browse all articles
        </button>
      </div>
    );
  }

  // No active slug → search + browse over the published corpus.
  const q = query.trim();
  const listed = q
    ? searchDocs(q, rows).map((r) => ({ slug: r.slug, title: r.title }))
    : rows.map((r) => ({ slug: r.slug, title: r.title }));

  return (
    <div className="flex flex-col">
      <div className="border-b border-border p-2">
        <div className="relative">
          <Search
            size={13}
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-ink-muted"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search help"
            aria-label="Search help"
            className="w-full rounded-md border border-border bg-surface-2 py-1.5 pl-7 pr-2 text-sm text-ink-primary placeholder:text-ink-muted focus:border-accent focus:outline-none"
          />
        </div>
      </div>
      <div className="space-y-0.5 p-2">
        {listed.length === 0 ? (
          <div className="px-3 py-6 text-center">
            {!q && <FileText size={20} className="mx-auto mb-2 text-ink-muted" />}
            <p className="text-sm text-ink-muted">
              {q ? 'No matching articles.' : 'Published help articles will appear here.'}
            </p>
          </div>
        ) : (
          listed.map((doc) => (
            <button
              key={doc.slug}
              type="button"
              onClick={() => onSelectSlug(doc.slug)}
              className="w-full truncate rounded-md px-3 py-2 text-left text-sm text-ink-secondary transition-colors hover:bg-surface-2 hover:text-ink-primary"
            >
              {doc.title}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
